import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import '../mobile.css';

const OTP_SECONDS = 90;

const normalizeIndianMobile = (raw) => {
  let s = String(raw || '').trim().replace(/\D/g, '');
  if (s.startsWith('91') && s.length > 10) s = s.slice(2);
  if (s.startsWith('0') && s.length > 10) s = s.slice(1);
  if (s.length > 10) s = s.slice(-10);
  return s;
};

const isValidIndianMobile = (raw) => /^[6-9]\d{9}$/.test(normalizeIndianMobile(raw));

const MobileLogin = ({ API_URL }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const otpRef = useRef(null);

  const [email, setEmail] = useState(location.state?.email || localStorage.getItem('email') || '');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [timeLeft, setTimeLeft] = useState(OTP_SECONDS);
  const [isOTPLoading, setIsOTPLoading] = useState(false);
  const [isLoginLoading, setIsLoginLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const isDisabled = isLoginLoading || !email || !password || email.length < 10;

  useEffect(() => {
    let timer;
    if (otpSent && timeLeft > 0) {
      timer = setInterval(() => setTimeLeft((prev) => prev - 1), 1000);
    } else if (otpSent && timeLeft <= 0) {
      setOtpSent(false);
      setTimeLeft(OTP_SECONDS);
    }
    return () => clearInterval(timer);
  }, [otpSent, timeLeft]);

  const handleEmailChange = (e) => {
    setEmail((e.target.value || '').replace(/\D/g, '').slice(0, 10));
  };

  const sendOTP = async () => {
    if (!isValidIndianMobile(email)) {
      setErrorMessage('Please enter a valid Indian mobile number.');
      return;
    }

    setPassword('');
    setOtpSent(true);
    setTimeLeft(OTP_SECONDS);
    setTimeout(() => otpRef.current?.focus(), 0);

    try {
      setIsOTPLoading(true);
      await axios.post(`${API_URL}/user/otp`, { mobile: normalizeIndianMobile(email) });
    } catch (err) {
      const data = err?.response?.data;
      const serverMsg = (typeof data === 'string' && data) || data?.message || data?.error || null;
      setErrorMessage(serverMsg || 'Something went wrong. Please try again.');
      setOtpSent(false);
    } finally {
      setIsOTPLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoginLoading(true);
    setErrorMessage('');
    localStorage.clear();

    try {
      const res = await fetch(`${API_URL}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, otp: true }),
      });

      if (!res.ok) {
        if (res.status === 403) {
          navigate('/m/reset-password', { state: { email } });
          return;
        }
        setErrorMessage(res.status === 401 ? 'Invalid Mobile or Password.' : 'An error occurred. Please try after some time.');
        return;
      }

      const data = await res.json();
      if (!data.token) {
        setErrorMessage('An error occurred. Please try after some time.');
        return;
      }

      const { jwtDecode } = await import('jwt-decode');
      const payload = jwtDecode(data.token);

      localStorage.setItem('jwtToken', data.token);
      localStorage.setItem('email', email);
      localStorage.setItem('role', payload?.role ?? '');
      localStorage.setItem('firstName', (payload?.firstName ?? '').toString().trim());
      localStorage.setItem('lastName', (payload?.lastName ?? '').toString().trim());
      localStorage.setItem('designation', (payload?.designation ?? '').toString().trim());
      localStorage.setItem('language', payload?.language ?? '');

      navigate('/m/cases');
    } catch (err) {
      setErrorMessage('An error occurred. Please try after some time.');
    } finally {
      setIsLoginLoading(false);
    }
  };

  return (
    <div>
      <h1 style={{ fontSize: '24px', fontWeight: 600, textAlign: 'center', marginBottom: '20px' }}>Login</h1>
      <form onSubmit={handleLogin}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
          <input
            value={email}
            placeholder="Mobile"
            inputMode="numeric"
            maxLength={10}
            onChange={handleEmailChange}
            required
            style={{ flex: 1, borderWidth: 1, borderStyle: 'solid', borderColor: '#ccc', borderRadius: '8px', padding: '12px' }}
          />
          <div style={{ width: '110px', flex: '0 0 110px' }}>
            {!otpSent ? (
              <button
                type="button"
                onClick={sendOTP}
                disabled={isOTPLoading || isLoginLoading || !email || email.length < 10}
                className="m-button-primary"
                style={{ height: '44px', padding: 0, fontSize: '14px', background: '#509acb' }}
              >
                {isOTPLoading ? <div className="m-spinner" style={{ margin: '0 auto' }} /> : 'Send OTP'}
              </button>
            ) : (
              <div
                style={{
                  height: '44px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '1px solid #ccc',
                  borderRadius: '8px',
                  fontWeight: 600,
                  color: '#094268',
                }}
              >
                {timeLeft}s
              </div>
            )}
          </div>
        </div>

        <div style={{ position: 'relative', marginBottom: '12px' }}>
          <input
            ref={otpRef}
            type={showPassword ? 'text' : 'password'}
            value={password}
            placeholder="OTP"
            onChange={(e) => setPassword(e.target.value)}
            required
            style={{ width: '100%', boxSizing: 'border-box', borderWidth: 1, borderStyle: 'solid', borderColor: '#ccc', borderRadius: '8px', padding: '12px', paddingRight: '40px' }}
          />
          <span
            onClick={() => setShowPassword(!showPassword)}
            style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', cursor: 'pointer', color: '#666' }}
          >
            {showPassword ? <FaEyeSlash size={18} /> : <FaEye size={18} />}
          </span>
        </div>

        <button type="submit" className="m-button-primary" disabled={isDisabled}>
          {isLoginLoading ? <div className="m-spinner" style={{ margin: '0 auto' }} /> : 'Login'}
        </button>

        <p style={{ textAlign: 'center', marginTop: '20px' }}>
          <button
            type="button"
            onClick={() => navigate('/m/register')}
            className="m-pill-btn"
            style={{ color: '#094268', fontWeight: 600 }}
          >
            New here? Register
          </button>
        </p>
      </form>

      {errorMessage && (
        <div className="m-popup-overlay">
          <div className="m-popup-container">
            <h3>Error</h3>
            <p>{errorMessage}</p>
            <button className="m-button-primary" onClick={() => setErrorMessage('')}>OK</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MobileLogin;
